"use client";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export interface AlertDialogProps {
  open: boolean;
  onClose: (value: boolean) => void;
  title: string;
  content: string | React.FC;
  confirmButtonTitle: string;
  closeButtonTitle?: string;
  customIcon?: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref">
  >;
  isLoading?: boolean;
  isConfirmButtonVisible?: boolean;
  testId?: string;
}

export default function AlertDialog(props: AlertDialogProps) {
  const {
    customIcon: CustomIcon,
    title,
    content: Content,
    confirmButtonTitle,
    closeButtonTitle = "Cancel",
    isLoading,
    isConfirmButtonVisible = true,
    testId,
  } = props;

  return (
    <Dialog open={props.open} onClose={props.onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-350 data-[leave]:duration-350 data-[enter]:ease-in-out data-[leave]:ease-in-out"
      />

      <div className="fixed inset-0 z-40 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-350 data-[leave]:duration-350 data-[enter]:ease-in-out data-[leave]:ease-in-out sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-lf-red-dark sm:mx-0 sm:h-10 sm:w-10">
                {CustomIcon && (
                  <CustomIcon
                    aria-hidden="true"
                    className="h-6 w-6 text-gray-200"
                  />
                )}
                {!CustomIcon && (
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="h-6 w-6 text-gray-200"
                  />
                )}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p
                    className="text-sm text-gray-500"
                    data-testid={testId ? `${testId}-content` : ""}
                  >
                    {typeof Content === "string" ? Content : <Content />}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              {isConfirmButtonVisible && (
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => props.onClose(true)}
                  className="inline-flex w-full justify-center rounded-md bg-lf-red-dark px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto cursor-pointer"
                  data-testid={testId ? `${testId}-confirm-btn` : ""}
                >
                  {!isLoading ? confirmButtonTitle : "Loading..."}
                </button>
              )}

              {!isLoading && (
                <button
                  type="button"
                  data-autofocus
                  onClick={() => props.onClose(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto cursor-pointer"
                  data-testid={testId ? `${testId}-close-btn` : ""}
                >
                  {closeButtonTitle}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
